import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import { NaiveDate, YearMonth } from "@tento-chrono";
import { LonaApi } from "../../api";
import type { WeatherLocations } from "@tento-lona";
import { RowKey, RowNetworkKey } from "@tento-lona/sheets";
import { WeatherDay } from "../weather-day";
import { WeatherLocationChoice } from "../weather-location-choice";


@component({
  name: "lona-weather-subtitle",
})
export class WeatherSubtitle extends LonaWebComponent {
  private fetchGeneration = 0;
  private disconnectGeneration = 0;

  override onRecycle(): void {
    this.disconnectGeneration++;
    this.fetchGeneration++;
    this.$weather.textContent = "";
    super.onRecycle();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    const generation = ++this.disconnectGeneration;
    queueMicrotask(() => {
      if (this.isConnected || generation !== this.disconnectGeneration) return;
      this.fetchGeneration++;
    });
  }

  private $location: HTMLElement = this.$("location");
  private $weather: HTMLElement = this.$("weather");

  static makeWith(props: WeatherSubtitle.Props): WeatherSubtitle {
    const $c = WeatherSubtitle.make();
    $c.bind(props);
    return $c;
  }

  bind(props: WeatherSubtitle.Props): this {
    const generation = ++this.fetchGeneration;
    const locationLabel =
      (props.lid == null ? null : props.locations.byId.get(props.lid)?.label) ?? "Unknown";
    this.$location.textContent = locationLabel;
    this.$weather.textContent = "";

    const rowKey = WeatherLocationChoice.previewRowKey(props);
    if (rowKey != null) this.fetchWeather(rowKey, props.date, generation);

    return this;
  }

  private async fetchWeather(rowKey: string, date: NaiveDate, generation: number): Promise<void> {
    const start = NaiveDate.fromYmd1Exp(date.yr, date.mth, 1);
    const dim = YearMonth.daysInMonth({ yr: date.yr, mth: date.mth });
    const end = start.addDays(dim);

    try {
      const cells = await LonaApi.get("/api/rows/{rowKey}/cells", {
        path: { rowKey: RowNetworkKey.fromWire(rowKey) },
        query: {
          start,
          end,
          mock_sync_delay: null,
          booking_id: null,
        },
      });

      if (generation !== this.fetchGeneration) return;
      const weatherDay = WeatherDay.dayOf(cells, date);

      if (weatherDay) {
        const temp = Math.round(weatherDay.avgtempF);
        const condition = weatherDay.condition.text;
        this.$weather.textContent = `${temp}°F · ${condition}`;
      }
    } catch (e) {
      // Silently fail - location is still shown
    }
  }

  static $styles: Style.Compat[] = [
    Typography.$style,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      #location {
        color: var(--text-ter);
      }

      #weather {
        color: var(--text-sec);
        --margin-top: 6px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <p id="location" class="small"></p>
    <p id="weather" class="small"></p>
  `;
}

export namespace WeatherSubtitle {
  export interface Props {
    lid: Option<number>;
    /** The table `lid` is looked up in (`client.getWeatherLocations()`). */
    locations: WeatherLocations;
    date: NaiveDate;
    rowKey?: Option<string>;
  }
}
